/**
 * DashboardWorkshopCreditCard — dashboard widget showing the current Thrive
 * member's monthly workshop credit state, reset date, and a link to the
 * month's workshop lineup.
 *
 * States:
 *   • credit available — big "1 credit ready" + browse workshops
 *   • credit used — 30% off benefit + reset date countdown
 *   • not thrive — upsell (rendered via WorkshopUpgradeUpsell instead)
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Ticket, ArrowRight, Calendar, Tag } from 'lucide-react';
import api from '@/lib/api';

function formatReset(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  } catch { return iso; }
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const diffMs = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  } catch { return null; }
}

export default function DashboardWorkshopCreditCard() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/workshops/me/credit');
        if (alive) setState(res.data);
      } catch {
        if (alive) setState(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading || !state || !state.is_thrive) return null;

  const days = daysUntil(state.next_reset_iso);
  const isAvailable = state.credit_available;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`rounded-3xl overflow-hidden shadow-card ${
        isAvailable
          ? 'bg-gradient-to-br from-teal to-teal-deep text-white'
          : 'bg-white border border-border-subtle text-navy'
      }`}
      data-testid="dashboard-workshop-credit-card"
    >
      <div className="p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isAvailable ? 'bg-white/15' : 'bg-teal/15'
            }`}>
              {isAvailable ? <Ticket size={20} /> : <Tag size={20} className="text-teal-deep" />}
            </div>
            <p className={`text-[11px] tracking-[0.2em] uppercase font-semibold ${
              isAvailable ? 'text-white/80' : 'text-teal-deep'
            }`}>
              {isAvailable ? 'This month' : 'Credit used'}
            </p>
          </div>
          {state.is_founding_member && (
            <span
              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                isAvailable
                  ? 'bg-white/15 text-white border border-white/25'
                  : 'bg-sand/30 text-navy border border-sand'
              }`}
              data-testid="founding-member-badge"
            >
              Founding
            </span>
          )}
        </div>

        {isAvailable ? (
          <>
            <h3 className="font-display text-3xl md:text-4xl leading-tight mb-2">
              1 workshop credit ready
            </h3>
            <p className="text-white/85 text-sm mb-5">
              Apply it at checkout to attend any workshop free. Resets {formatReset(state.next_reset_iso)}.
            </p>
            <Link
              to="/my-city?tab=workshops"
              className="inline-flex items-center gap-2 bg-white text-teal-deep hover:bg-cream rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
              data-testid="dashboard-browse-workshops-btn"
            >
              <Sparkles size={14} />
              Browse this month&apos;s workshops
              <ArrowRight size={14} />
            </Link>
          </>
        ) : (
          <>
            <h3 className="font-display text-2xl md:text-3xl leading-tight mb-2">
              {state.member_discount_pct}% off any additional workshops
            </h3>
            <p className="text-text-mid text-sm mb-4">
              You&apos;ve used this month&apos;s included workshop. Attend more at member pricing until your next credit unlocks.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-2 bg-cream rounded-full px-3 py-1.5 border border-border-subtle text-xs">
                <Calendar size={12} className="text-teal-deep" />
                <span>Next credit in {days === 0 ? 'today' : `${days} ${days === 1 ? 'day' : 'days'}`}</span>
              </div>
              <Link
                to="/my-city?tab=workshops"
                className="inline-flex items-center gap-2 text-teal-deep hover:text-teal text-sm font-medium"
                data-testid="dashboard-view-workshops-link"
              >
                View workshops <ArrowRight size={14} />
              </Link>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
